import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMigrationSites, createMigrationSite } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, ChevronRight, MapPin, ClipboardList, Wand2, Route, Rocket, LayoutGrid,
  CheckCircle2, Circle, ArrowRight, Plus,
} from "lucide-react";

const STEPS = [
  { n: 1, icon: MapPin, title: "Add your locations", to: "/migration",
    body: "Create each real site you'll migrate. Everything else — runbooks, scoreboard, waves — is built per location." },
  { n: 2, icon: ClipboardList, title: "Capture discovery", to: "/runbook",
    body: "For each site, fill the Discovery Questionnaire (existing PSTN gateway, Local Gateway, users, DIDs). It drives your plan." },
  { n: 3, icon: Wand2, title: "Prefill from discovery", to: "/runbook",
    body: "On the Prepare phase, hit 'Prefill from Discovery' to auto-tick inventory tasks from your E911, Rooms and site data." },
  { n: 4, icon: Route, title: "Work the runbook & gates", to: "/runbook",
    body: "Progress the 7 PPDIO phases. Each gate is enforced — you can't advance until tasks pass. Ask the AI Guide for the exact commands." },
  { n: 5, icon: LayoutGrid, title: "Group into waves & watch the estate", to: "/scoreboard",
    body: "Assign each site a migration wave, then track every location's progress in one colour-coded Estate Scoreboard." },
  { n: 6, icon: Rocket, title: "Cutover & sign off", to: "/waves",
    body: "Run the cutover-day runsheet per wave and sign off once every site's Cutover gate is approved." },
];

export default function GetStarted() {
  const [sites, setSites] = useState([]);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("lvai_onboarded", "1"); load(); }, []);
  const load = () => getMigrationSites().then(setSites).catch(() => {});

  const addFirst = async () => {
    const name = window.prompt("Name your first location (e.g. 'HQ — London'):", "");
    if (!name || !name.trim()) return;
    setAdding(true);
    try { await createMigrationSite({ name: name.trim() }); toast.success("Location added"); load(); }
    catch { toast.error("Could not add site"); }
    setAdding(false);
  };

  const hasSites = sites.length > 0;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> get started
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-3xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Sparkles className="h-4 w-4" /> Kickoff
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Welcome to UC Engineer Pro</h1>
        <p className="text-zinc-400 mt-3 text-base">
          Your AI-powered workspace to troubleshoot, configure, migrate and validate Cisco &amp; Cloud UC — from live field diagnostics to full CUCM → Webex Calling migrations. It's a clean slate —
          here's exactly how to get going.
        </p>

        {/* First action */}
        <div className="mt-6 rounded-lg border border-blue-600/30 bg-blue-600/5 p-5" data-testid="kickoff-first-action">
          {hasSites ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[13px] text-zinc-200">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                {sites.length} location{sites.length > 1 ? "s" : ""} added. You're set — continue below.
              </div>
              <button onClick={() => navigate("/migration")} data-testid="kickoff-manage-sites" className="flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                Manage locations <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-bold text-white">Start by adding your first location</div>
                <div className="text-[13px] text-zinc-400 mt-0.5">Everything in the app is organised per site.</div>
              </div>
              <button onClick={addFirst} disabled={adding} data-testid="kickoff-add-first" className="flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white">
                <Plus className="h-4 w-4" /> {adding ? "Adding…" : "Add first location"}
              </button>
            </div>
          )}
        </div>

        {/* Steps */}
        <div className="mt-6 space-y-2.5" data-testid="kickoff-steps">
          {STEPS.map((s) => (
            <button key={s.n} onClick={() => navigate(s.to)} data-testid={`kickoff-step-${s.n}`}
              className="w-full text-left flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900 hover:border-zinc-600 transition-colors p-4">
              <span className="h-9 w-9 rounded-md bg-zinc-950 border border-zinc-700 flex items-center justify-center shrink-0">
                <s.icon className="h-4 w-4 text-blue-400" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-zinc-500">Step {s.n}</span>
                  <span className="text-sm font-bold text-white">{s.title}</span>
                </div>
                <div className="text-[13px] text-zinc-400 mt-1">{s.body}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0 mt-1" />
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-[13px] text-zinc-400">
          <span className="text-white font-semibold">Tips:</span> Install this as an app from your browser's menu (or the “Install app” button in the sidebar) — it auto-updates on every launch. The bell icon shows live alerts for expiring certificates, licenses and E911 gaps.
          <button onClick={() => navigate("/capabilities")} data-testid="kickoff-capabilities" className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-blue-400 hover:text-blue-300">
            <ArrowRight className="h-3.5 w-3.5" /> View / download the full capabilities PDF
          </button>
        </div>
      </div>
    </div>
  );
}
